import {
  ReactElement, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  ContainerPage, MachineProfileContainer,
} from './styles';
import { useTranslation } from 'react-i18next';
import { getUserProfile } from 'helpers/userProfile';
import { useHistory, useParams } from 'react-router-dom';
import {
  formatHealthIndexToStatus,
  getDefaultSetpointOnChangeMode,
  getIRSetpointCloseOrBelow, getMachineInfo, getSortedDutSetpoints, parseOperationToType, setMachineConfig,
} from './helpers';
import { ApiParams, ApiResps } from '~/providers';
import { HeaderMachine } from './components/HeaderMachine';
import { EnvironmentItem, Operation, StatusCardOptions } from './types';
import { BlockedModeContainer } from './components/BlockedModeContent';
import FloatSaveButton from './components/FloatSaveButton';
import { useVerifyOutOfSchedule } from './hooks/useVerifyOutOfSchedule';
import { SetpointContent } from './SetpointContent';
import { OutOfScheduleContent } from './components/OutOfScheduleContent';
import { useWebsocketStatusByMachine } from './hooks/useWebsocketStatusByMachine';
import { ModalSaveChanges } from '~/components/ModalsRemoteControl/SaveChanges';
import { ModalErrorSendingInfoMachine } from '~/components/ModalsRemoteControl/ErrorSendInfoMachine';
import { ModalSendingConfigs } from '~/components/ModalsRemoteControl/SendingConfigs';
import { useNotify } from '~/contexts/notificationPopUp';
import { ProfileContent } from './ProfileContent';
import { ModalExitWithoutSaving } from '~/components/ModalsRemoteControl/ExitWithoutSaving';
import { OperationType, StatusType } from '../StatusCard';
import { ReasonModal } from '~/components/ModalsRemoteControl/ReasonModal';
import { useReasonsModalInfo } from './hooks/useReasonsModalInfo';
import { RemoteControlReason } from 'providers/types/remoteControl';

export function MachineProfileCR(): ReactElement {
  const profile = getUserProfile();

  const [openSaveChangesModal, setOpenSaveChangesModal] = useState(false);
  const [checkAutomaticMode, setCheckAutomaticMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [environments, setEnvironments] = useState<EnvironmentItem[]>([]);
  const [machineInfo, setMachineInfo] = useState<{name: string; environmentName: string, deviceCode: string}>();
  const [limits, setLimits] = useState<{tMin: number, tMax: number}>();
  const [currentOperation, setCurrentOperation] = useState<Operation>();
  const [currentSetpoint, setCurrentSetpoint] = useState<number>();
  const [currentReason, setCurrentReason] = useState<RemoteControlReason | undefined>();
  const [automationIntervals, setAutomationIntervals] = useState<{startTime: string, endTime: string}[]>([]);
  const [timezone, setTimezone] = useState<number>();
  const [defaultValues, setDefaultValues] = useState<ApiResps['/remote-control/get-machine-info']>();
  const [availableSetpoints, setAvailableSetpoints] = useState<{id: string, setpoint: number}[]>([]);
  const [sentNewInfo, setSentNewInfo] = useState<{ operation?: OperationType, status?: StatusType } | undefined>();

  const [openSendConfigModal, setOpenSendConfigModal] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [openExitModal, setOpenExitModal] = useState(false);
  const [openEconomyModeOffModal, setOpenEconomyModeOffModal] = useState(false);

  const [isError, setIsError] = useState(false);

  const hasEditedValuesRef = useRef<boolean>(false);
  const history = useHistory<{
    MACHINE_NAME: string,
    environmentName: string,
    deviceCode: string
  } | {}>();
  const { machineId } = useParams<{ machineId: string }>();
  const { t } = useTranslation();
  const { isOutOfSchedule } = useVerifyOutOfSchedule(automationIntervals, timezone);
  const {
    loading: loadingDeviceStatus, connection, operation, status, error: errorDeviceStatus,
  } = useWebsocketStatusByMachine(machineInfo?.deviceCode);
  const { notify } = useNotify();
  const {
    onCloseReasonModal,
    onOpenReasonModal,
    openReasonModal,
    reasonsList,
    iconModal,
    titleModal,
    mode: reasonMode,
  } = useReasonsModalInfo();

  const generalLoading = useMemo(() => loading || loadingDeviceStatus, [loading, loadingDeviceStatus]);

  const blockedOperation = useMemo(() => defaultValues?.OPERATION === 'blocked', [defaultValues]);

  function goToMachineList(): void {
    history.push('/controle-inteligente');
  }

  function isValidChanges(): boolean {
    if (!defaultValues) {
      notify(t('naoFoiPossivelSalvarAlteracoes'), 'error');
      onCloseSaveChangesModal();
      return false;
    }

    if (!checkAutomaticMode && currentSetpoint && currentOperation === 'cool') {
      if (limits && (limits.tMax < currentSetpoint || limits.tMin > currentSetpoint)) {
        notify(t('setpointEscolhidoNaoEstaLimites'), 'error');
        return false;
      }
    }

    return true;
  }

  async function saveChanges(): Promise<{
    economyEnabled?: boolean;
    operation?: string;
    setpoint?: number
  } | undefined> {
    if (!defaultValues) {
      return undefined;
    }

    const closeSetpoint = getIRSetpointCloseOrBelow(availableSetpoints, currentSetpoint);
    const newSetpoint = closeSetpoint?.setpoint ?? currentSetpoint;

    const reqParams: ApiParams['/remote-control/set-config'] = {
      MACHINE_ID: Number(machineId),
      DEV_AUT_CODE: defaultValues.DEV_AUT_CODE,
      ECONOMY_ENABLED: checkAutomaticMode,
      ...(checkAutomaticMode ? {
        OPERATION: undefined,
        IR_ID_COOL: undefined,
        SETPOINT: undefined,
      } : {
        OPERATION: currentOperation,
        SETPOINT: newSetpoint,
        IR_ID_COOL: closeSetpoint?.id,
        REASON: currentReason,
      }),
    };
    const response = await setMachineConfig(reqParams);

    const isAutomaticModeValid = response?.ECONOMY_ENABLED === reqParams.ECONOMY_ENABLED;
    const isOperationValid = response?.OPERATION === reqParams.OPERATION;
    const isSetpointValid = response?.SETPOINT === reqParams.SETPOINT;

    if (isAutomaticModeValid && isOperationValid && isSetpointValid) {
      return {
        economyEnabled: response.ECONOMY_ENABLED,
        operation: response.OPERATION,
        setpoint: response.SETPOINT,
      };
    }

    return undefined;
  }

  async function onClickSaveModal(): Promise<void> {
    if (!hasEditedValues) {
      return goToMachineList();
    }

    if (!isValidChanges()) {
      onCloseSaveChangesModal();
      return;
    }
    onCloseSaveChangesModal();
    setOpenSendConfigModal(true);

    try {
      const response = await saveChanges();

      setOpenSendConfigModal(false);
      if (response) {
        notify(t('alteracoesRealizadasComSucesso'), 'success');
        setNewInfoRealTime(response.operation, 'on');
        resetValues(false, response.operation, response.setpoint);
        handleCloseSetpoint(response.setpoint);
        return;
      }

      throw new Error('Error sending config.');
    } catch (err) {
      setOpenSendConfigModal(false);
      setOpenErrorModal(true);
    }
  }

  function handleCloseSetpoint(setpointChanged?: number): void {
    if (currentSetpoint && setpointChanged && currentSetpoint !== setpointChanged) {
      setCurrentSetpoint(setpointChanged);
    }
  }

  function setNewInfoRealTime(operation?: string, status?: 'off' | 'on'): void {
    if (defaultValues?.DEV_AUT_CODE.startsWith('DAM') && operation === 'cool') return;

    let newOperation;
    if (operation && operation !== 'disabled') {
      newOperation = parseOperationToType(operation);
    }

    setSentNewInfo({
      operation: newOperation,
      status,
    });
  }

  function resetValues(economyMode?: boolean, operation?: string, setpoint?: number): void {
    hasEditedValuesRef.current = false;

    const newSetpoint = machineInfo?.deviceCode.startsWith('DAM')
      ? { SETPOINT_EXCEPTION_ECO: setpoint } : { SETPOINT: setpoint };

    if (defaultValues) {
      const newValue = {
        ...defaultValues,
        ...(economyMode !== undefined && { ECONOMY_ENABLED: economyMode }),
        ...(operation && { OPERATION: operation }),
        ...(setpoint && newSetpoint),
      };

      setDefaultValues(newValue as typeof defaultValues);
    }
  }

  function onCloseSaveChangesModal(): void {
    setOpenSaveChangesModal(false);
  }

  function onClickEnableEconomyMode(): void {
    if (!defaultValues) return;
    setOpenSendConfigModal(true);

    setMachineConfig({
      DEV_AUT_CODE: defaultValues.DEV_AUT_CODE,
      ECONOMY_ENABLED: true,
      MACHINE_ID: defaultValues.MACHINE_ID,
    }).then((value) => {
      setOpenSendConfigModal(false);
      if (value) {
        notify(t('alteradoParaModoEconomia'), 'success');
        setCheckAutomaticMode(true);
        setCurrentSetpoint(defaultValues.SETPOINT);
        setCurrentReason(undefined);

        if (defaultValues.PREVIOUS_OPERATION) {
          setCurrentOperation(defaultValues.PREVIOUS_OPERATION);
          setNewInfoRealTime(undefined, undefined);
          resetValues(true, defaultValues.PREVIOUS_OPERATION);
        } else {
          setCurrentOperation(defaultValues.OPERATION);
          setNewInfoRealTime(defaultValues.OPERATION, defaultValues.OPERATION === 'disabled' ? 'off' : 'on');
          resetValues(true);
        }
      } else {
        setOpenErrorModal(true);
      }
    });
  }

  function onDisabledEconomyMode(reason: RemoteControlReason): void {
    setCurrentReason(reason);
    if (blockedOperation) {
      setCurrentOperation('cool');
      setCurrentSetpoint(getAverageSetpointAvailable());
    } else if (limits && currentSetpoint && (currentSetpoint > limits.tMax || currentSetpoint < limits.tMin)) {
      setCurrentSetpoint(getDefaultSetpointOnChangeMode(limits, currentSetpoint));
    }
    setCheckAutomaticMode(false);
    notify(t('modoEconomiaDesligadoSucesso'), 'success');
  }

  function onClickModality(mode: 'cool' | 'fan'): void {
    hasEditedValuesRef.current = true;
    if (mode === 'cool' && limits) {
      if (defaultValues?.SETPOINT) {
        setCurrentSetpoint(getDefaultSetpointOnChangeMode(limits, defaultValues.SETPOINT));
      } else {
        setCurrentSetpoint(getAverageSetpointAvailable());
      }
    }

    if (mode === 'fan') {
      setCurrentSetpoint(undefined);
    }
    setCurrentOperation(mode);
  }

  function onClickCheckAutomaticMode(): void {
    setOpenEconomyModeOffModal(false);
    const newValue = !checkAutomaticMode;
    if (!newValue) {
      hasEditedValuesRef.current = true;
      onOpenReasonModal('economyModeDisabled');
    } else {
      onClickEnableEconomyMode();
    }
  }

  function onClickPower(): void {
    hasEditedValuesRef.current = true;
    onOpenReasonModal(currentOperation === 'disabled' ? 'on' : 'off');
  }

  function closeReasonModal(): void {
    if (!hasEditedValues()) {
      hasEditedValuesRef.current = false;
    }
    onCloseReasonModal();
  }

  function onSelectedReason(reason: RemoteControlReason): void {
    switch (reasonMode) {
      case 'economyModeDisabled': {
        onDisabledEconomyMode(reason);
        break;
      }
      case 'on': {
        onClickOnDevice(reason);
        break;
      }
      case 'off': {
        onClickOffDevice(reason);
        break;
      }
      default: throw new Error(`Reason mode ${reasonMode} not found.`);
    }

    closeReasonModal();
  }

  function onClickOnDevice(reason: RemoteControlReason): void {
    if (!defaultValues) return;

    const averageSetpoint = getAverageSetpointAvailable();
    const closeSetpoint = getIRSetpointCloseOrBelow(availableSetpoints, averageSetpoint);
    const newSetpoint = closeSetpoint?.setpoint ?? averageSetpoint;

    setOpenSendConfigModal(true);
    setMachineConfig({
      DEV_AUT_CODE: defaultValues.DEV_AUT_CODE,
      MACHINE_ID: Number(machineId),
      ECONOMY_ENABLED: false,
      OPERATION: 'cool',
      SETPOINT: newSetpoint,
      IR_ID_COOL: closeSetpoint?.id,
      REASON: reason,
    }).then((values) => {
      setOpenSendConfigModal(false);
      if (values && values.ECONOMY_ENABLED === false && values.OPERATION === 'cool' && values.SETPOINT === newSetpoint) {
        setCurrentOperation('cool');
        setCurrentSetpoint(newSetpoint);
        setNewInfoRealTime(values.OPERATION, 'on');
        resetValues(false, values.OPERATION, values.SETPOINT);

        notify(t('maquinaLigadaComSucesso'), 'success');
        return;
      }

      setOpenErrorModal(true);
    }).catch(() => {
      setOpenSendConfigModal(false);
      setOpenErrorModal(true);
    });
  }

  function onClickOffDevice(reason: RemoteControlReason): void {
    if (!defaultValues) return;
    setOpenSendConfigModal(true);
    setMachineConfig({
      DEV_AUT_CODE: defaultValues.DEV_AUT_CODE,
      MACHINE_ID: Number(machineId),
      ECONOMY_ENABLED: false,
      OPERATION: 'disabled',
      REASON: reason,
    }).then((values) => {
      setOpenSendConfigModal(false);
      if (values && values.ECONOMY_ENABLED === false && values.OPERATION === 'disabled') {
        setCurrentOperation('disabled');
        setNewInfoRealTime(undefined, 'off');
        resetValues(false, values.OPERATION);

        notify(t('maquinaDesligadaComSucesso'), 'success');
        return;
      }

      setOpenErrorModal(true);
    }).catch(() => {
      setOpenSendConfigModal(false);
      setOpenErrorModal(true);
    });
  }

  function onChangeSetpoint(newSetpoint: number | undefined): void {
    hasEditedValuesRef.current = true;
    setCurrentSetpoint(newSetpoint);
  }

  async function getMachine(needMachineInfo: boolean): Promise<void> {
    const permissionUnit = profile.permissions.PER_UNIT?.[0];

    if (!permissionUnit) return;

    const unitId = permissionUnit.units[0];

    const response = await getMachineInfo({
      MACHINE_ID: Number(machineId),
      UNIT_ID: unitId,
    });

    if (!response) {
      setIsError(true);
      return;
    }

    setDefaultValues({
      ...response,
      ...(response.PREVIOUS_OPERATION && { OPERATION: response.PREVIOUS_OPERATION }),
    });

    setTimezone(response.TIMEZONE_OFFSET);

    if (needMachineInfo) {
      setMachineInfo({
        name: response.MACHINE_NAME,
        environmentName: response.ENVIRONMENTS.length > 0 ? response.ENVIRONMENTS[0].ENVIRONMENT_NAME : '',
        deviceCode: response.DEV_AUT_CODE,
      });
    }

    setEnvironments(response.ENVIRONMENTS.map((env) => ({
      id: env.ENVIRONMENT_ID,
      name: env.ENVIRONMENT_NAME,
      dutCode: env.DUT_CODE,
      selfReference: !!env.SELF_REFERENCE,
      returnTemperatureField: env.RETURN_TEMPERATURE_FIELD,
    })));
    setLimits({
      tMax: response.MAXIMUM_SETPOINT,
      tMin: response.MINIMUM_SETPOINT,
    });
    setCurrentOperation(response.OPERATION ?? undefined);

    if (response.OPERATION !== 'blocked') {
      setNewInfoRealTime(response.OPERATION, response.OPERATION === 'disabled' ? 'off' : 'on');
    }

    if (response.LAST_REASON) {
      setCurrentReason(response.LAST_REASON);
    }

    setCurrentSetpoint(response.DEV_AUT_CODE.startsWith('DAM') && !response.ECONOMY_ENABLED ? response.SETPOINT_EXCEPTION_ECO : response.SETPOINT);
    setCheckAutomaticMode(response.ECONOMY_ENABLED);
    setAutomationIntervals(response.AUTOMATIONS_INTERVALS.map((interval) => ({
      endTime: interval.END,
      startTime: interval.START,
    })));

    await getDutIrSetpoints(response.DEV_AUT_CODE);

    setLoading(false);
  }

  async function getDutIrSetpoints(deviceCode: string): Promise<void> {
    if (!deviceCode.startsWith('DUT')) return;

    const setpoints = await getSortedDutSetpoints(deviceCode);

    if (setpoints.length === 0) {
      setIsError(true);
      return;
    }

    setAvailableSetpoints(setpoints);
  }

  function getAverageSetpointAvailable(): number | undefined {
    if (limits) {
      const middleSetpoint = Math.floor((limits.tMin + limits.tMax) / 2);
      return middleSetpoint;
    }

    return undefined;
  }

  function onCloseErrorModal(): void {
    setOpenErrorModal(false);
  }

  function onCloseExitModal(): void {
    onPushHistoryState();
    setOpenExitModal(false);
  }

  function onExit(): void {
    goToMachineList();
  }

  function onClickBackButton(): void {
    if (hasEditedValuesRef.current) {
      setOpenExitModal(true);
    } else {
      goToMachineList();
    }
  }

  const getDefaultSetpoint = useCallback((): number | undefined => {
    if (!machineInfo?.deviceCode) return undefined;
    if (!defaultValues) return undefined;

    if (machineInfo.deviceCode.startsWith('DAM') && !defaultValues.ECONOMY_ENABLED) {
      return defaultValues.SETPOINT_EXCEPTION_ECO;
    }
    return defaultValues.SETPOINT;
  }, [defaultValues, machineInfo?.deviceCode]);

  const hasEditedValues = useCallback((): boolean => {
    if (!defaultValues) return false;

    if (checkAutomaticMode) return false;

    return defaultValues.OPERATION !== currentOperation
    || currentOperation === 'cool' && getDefaultSetpoint() !== currentSetpoint;
  }, [checkAutomaticMode, currentOperation, currentSetpoint, defaultValues, getDefaultSetpoint]);

  function renderContainer(): ReactElement {
    if (isOutOfSchedule && !generalLoading) {
      return <OutOfScheduleContent automationIntervals={automationIntervals} />;
    }

    if (currentOperation === 'blocked' && checkAutomaticMode && !generalLoading) {
      return <BlockedModeContainer />;
    }

    return (
      <SetpointContent
        devAutCode={defaultValues?.DEV_AUT_CODE}
        checkAutomaticMode={checkAutomaticMode}
        environments={environments}
        loading={generalLoading}
        onChangeSetpoint={onChangeSetpoint}
        onClickModality={onClickModality}
        onPowerChange={onClickPower}
        currentOperation={currentOperation}
        currentSetpoint={currentSetpoint}
        limits={limits}
        onClickTurnOffEconomyMode={() => setOpenEconomyModeOffModal(true)}
      />
    );
  }

  function generateStatusOptions(): StatusCardOptions {
    if (currentOperation === 'blocked') {
      return {
        connection,
        operation: undefined,
        status: undefined,
        healthStatus: undefined,
      };
    }

    return {
      connection,
      operation: sentNewInfo ? sentNewInfo.operation : operation,
      status: sentNewInfo ? sentNewInfo.status : status,
      healthStatus: formatHealthIndexToStatus(defaultValues?.H_INDEX ?? 0),
    };
  }

  function handleBeforeUnload(event: BeforeUnloadEvent): void {
    if (hasEditedValuesRef.current) {
      event.preventDefault();
    }
  }

  function onPushHistoryState(): void {
    window.history.pushState(null, '', window.location.href);
  }

  function handleBackButtonEvent(event: PopStateEvent): void {
    event.preventDefault();
    if (hasEditedValuesRef.current) {
      onClickBackButton();
    } else {
      window.history.back();
    }
  }

  useEffect(() => {
    if (errorDeviceStatus && !isError) {
      setIsError(true);
    }
  }, [errorDeviceStatus]);

  useEffect(() => {
    onPushHistoryState();
    window.addEventListener('beforeunload', handleBeforeUnload);

    window.addEventListener('popstate', handleBackButtonEvent);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handleBackButtonEvent);
    };
  }, []);

  useEffect(() => {
    let needMachineInfo = true;
    if (history.location.state) {
      if (typeof history.location.state === 'object'
        && 'MACHINE_NAME' in history.location.state
        && 'environmentName' in history.location.state
        && 'deviceCode' in history.location.state) {
        needMachineInfo = false;
        setMachineInfo({
          name: history.location.state.MACHINE_NAME,
          deviceCode: history.location.state.deviceCode,
          environmentName: history.location.state.environmentName,
        });
      }
    }

    if (machineId) {
      getMachine(needMachineInfo);
    }

    return () => {
      history.location.state = {};
    };
  }, []);

  useEffect(() => {
    if (sentNewInfo) {
      if (sentNewInfo.operation === operation) {
        setSentNewInfo(undefined);
      }
    }
  }, [sentNewInfo, operation]);

  return (
    <MachineProfileContainer>
      <ModalExitWithoutSaving
        isOpen={openExitModal}
        onClose={onCloseExitModal}
        onExit={onExit}
      />
      <ModalSaveChanges
        isOpen={openSaveChangesModal}
        onClose={onCloseSaveChangesModal}
        actionButton={onClickSaveModal}
      />
      <ModalErrorSendingInfoMachine
        isOpen={openErrorModal}
        onClose={onCloseErrorModal}
      />
      <ModalSendingConfigs
        isOpen={openSendConfigModal}
        onClose={() => {}}
      />
      <ReasonModal
        reasonList={reasonsList}
        title={titleModal}
        iconTitle={iconModal}
        isOpen={openReasonModal}
        onSelectReason={onSelectedReason}
        onClose={closeReasonModal}
      />
      <ContainerPage>
        <HeaderMachine
          name={machineInfo?.name ?? ''}
          environmentName={machineInfo?.environmentName ?? ''}
          loading={generalLoading}
          statusCardOptions={generateStatusOptions()}
          error={isError}
          onClickBackButton={onClickBackButton}
        />
        <ProfileContent
          automationIntervals={automationIntervals}
          checkAutomaticMode={checkAutomaticMode}
          connection={connection}
          generalLoading={generalLoading}
          isOutOfSchedule={isOutOfSchedule}
          isError={isError}
          onClickCheckAutomaticMode={onClickCheckAutomaticMode}
          openEconomyModeOffModal={openEconomyModeOffModal}
          onCloseEconomyModeOff={() => setOpenEconomyModeOffModal(false)}
        >
          {renderContainer()}
        </ProfileContent>
      </ContainerPage>
      {hasEditedValues() && !isOutOfSchedule && connection && (
        <FloatSaveButton
          onClickSave={() => setOpenSaveChangesModal(true)}
        />
      )}
    </MachineProfileContainer>
  );
}
